<template>
  <div id="app">
    <!-- Navigation -->
    <nav class="navbar">
      <div class="container">
        <div class="logo">F1 Start Timer</div>
        <div class="nav-links">
          <a href="/">Home</a>
          <a href="/f1-start-procedure">F1 Start Rules</a>
          <a href="/improve-reaction-time">Training Tips</a>
          <a href="/about">About</a>
        </div>
      </div>
    </nav>

    <!-- Hero Section -->
    <section class="hero" v-on:click="onClick">
      <div class="container">
        <h1>F1 Start Timer - Test Your Reaction Speed</h1>
        <p class="subtitle">Experience the thrill of an F1 start with our free online f1 start timer. Click anywhere to begin, watch the five red lights illuminate one by one, then click again the instant they go out. Can you match the lightning-fast reflexes of professional F1 drivers who react in just 150-250 milliseconds?</p>

        <!-- Game Component -->
        <div class="game-area">
          <div class="help">
            <p><em>Click or tap anywhere to start. Click again when lights go off.</em></p>
          </div>

          <div id="lights-container">
            <div id="connector"></div>
            <LightStrip ref="lights" v-for="id in [0, 1, 2, 3, 4]" :key="id" />
          </div>

          <h2 class="time">{{ result !== null ? result : "" }}</h2>
          <div class="best-score">Your best: {{ format(best) }}</div>
        </div>
      </div>
    </section>

    <!-- How It Works Section -->
    <section class="how-it-works">
      <div class="container">
        <h2>How It Works</h2>
        <p class="section-intro">Our f1 lights reaction test replicates the authentic Formula 1 starting procedure used in every Grand Prix:</p>

        <div class="steps">
          <div class="step">
            <div class="step-number">1</div>
            <h3>Click to Start</h3>
            <p>Tap or click anywhere on the screen to begin the sequence</p>
          </div>
          <div class="step">
            <div class="step-number">2</div>
            <h3>Watch the Lights</h3>
            <p>Five sets of red lights illuminate at 1-second intervals, just like on a real F1 grid</p>
          </div>
          <div class="step">
            <div class="step-number">3</div>
            <h3>Stay Ready</h3>
            <p>After all lights are on, they'll go out after a random delay (0.2-3 seconds) - you can't predict when!</p>
          </div>
          <div class="step">
            <div class="step-number">4</div>
            <h3>React Fast</h3>
            <p>Click the instant the lights go out to record your f1 reaction time test</p>
          </div>
          <div class="step">
            <div class="step-number">5</div>
            <h3>Check Your Score</h3>
            <p>See your reaction time in milliseconds and compare it to your personal best</p>
          </div>
        </div>

        <p class="warning"><strong>Warning:</strong> Click too early and you'll trigger a "JUMP START!" - just like in real F1 racing where drivers face penalties for false starts.</p>
      </div>
    </section>

    <!-- Benchmark Section -->
    <section class="benchmark">
      <div class="container">
        <h2>Can You Beat an F1 Driver?</h2>
        <p class="section-intro">Professional F1 drivers are among the fastest reactors in the world. Here's what you're up against:</p>

        <div class="benchmark-cards">
          <div class="benchmark-card">
            <div class="benchmark-label">Top Performers</div>
            <div class="benchmark-time">100-200ms</div>
            <div class="benchmark-desc">Elite F1 Drivers</div>
          </div>
          <div class="benchmark-card">
            <div class="benchmark-label">Average F1 Driver</div>
            <div class="benchmark-time">150-250ms</div>
            <div class="benchmark-desc">Professional Level</div>
          </div>
          <div class="benchmark-card">
            <div class="benchmark-label">Typical Human</div>
            <div class="benchmark-time">250-300ms</div>
            <div class="benchmark-desc">Average Person</div>
          </div>
        </div>

        <p class="benchmark-note">Even a 0.2-second delay at the start can cost multiple positions on the grid. F1 drivers train relentlessly to shave every millisecond off their reaction time.</p>
      </div>
    </section>

    <!-- Tips Section -->
    <section class="tips">
      <div class="container">
        <h2>Tips to Improve Your Reaction Time</h2>
        <p class="section-intro">Want to get faster? Here are proven methods to sharpen your reflexes:</p>

        <div class="tips-grid">
          <div class="tip-card">
            <h3>Practice Regularly</h3>
            <p>Use this f1 reaction time test daily. Like F1 drivers, consistency is key. Your brain builds neural pathways through repetition.</p>
          </div>
          <div class="tip-card">
            <h3>Stay Focused</h3>
            <p>Eliminate distractions and concentrate fully on the lights. F1 drivers train their mental focus under extreme pressure.</p>
          </div>
          <div class="tip-card">
            <h3>Get Enough Sleep</h3>
            <p>Sleep deprivation is the #1 killer of reaction speed. Aim for 7-9 hours per night to keep your reflexes sharp.</p>
          </div>
          <div class="tip-card">
            <h3>Cognitive-Motor Training</h3>
            <p>Combine physical movement with mental tasks. This trains your brain to process signals faster while your body is active.</p>
          </div>
          <div class="tip-card">
            <h3>Stay Hydrated</h3>
            <p>Even mild dehydration impairs brain function. Keep water nearby during your f1 lights reaction test sessions.</p>
          </div>
        </div>

        <a href="/improve-reaction-time" class="cta-link">Learn more advanced techniques →</a>
      </div>
    </section>

    <!-- FAQ Section -->
    <section class="faq">
      <div class="container">
        <h2>Frequently Asked Questions</h2>

        <div class="faq-item">
          <h3>What is a good reaction time for the f1 start timer?</h3>
          <p>A good reaction time is under 250ms. Elite F1 drivers achieve 150-250ms, while average humans score 250-300ms. If you consistently hit under 200ms, you're in world-class territory!</p>
        </div>

        <div class="faq-item">
          <h3>How does the f1 lights reaction test work?</h3>
          <p>The test replicates the real F1 starting procedure: five red lights illuminate at 1-second intervals, then go out after a random 0.2-3 second delay. You must click the instant they disappear to measure your reaction speed.</p>
        </div>

        <div class="faq-item">
          <h3>Why do I get "JUMP START" on the f1 reaction time test?</h3>
          <p>You clicked before the lights went out - that's a false start! In real F1 racing, this results in penalties. Wait for all lights to go out completely before clicking.</p>
        </div>

        <div class="faq-item">
          <h3>Can I improve my f1 start timer score?</h3>
          <p>Yes! Regular practice, proper sleep (7-9 hours), staying hydrated, and maintaining focus all improve reaction time. F1 drivers train daily to maintain their edge.</p>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="cta-section">
      <div class="container">
        <h2>Ready to Improve Your Skills?</h2>
        <p>Want to take your reaction time to the next level?</p>
        <div class="cta-buttons">
          <a href="/f1-start-procedure" class="btn-primary">Learn F1 Start Procedure</a>
          <a href="/improve-reaction-time" class="btn-secondary">Advanced Training Tips</a>
        </div>
      </div>
    </section>

    <!-- Footer -->
    <footer class="footer">
      <div class="container">
        <div class="footer-content">
          <div class="footer-col">
            <h4>Quick Links</h4>
            <a href="/">Home</a>
            <a href="/about">About</a>
            <a href="/privacy">Privacy Policy</a>
          </div>
          <div class="footer-col">
            <h4>Content</h4>
            <a href="/f1-start-procedure">F1 Start Procedure</a>
            <a href="/improve-reaction-time">Improve Reaction Time</a>
          </div>
          <div class="footer-col">
            <h4>About</h4>
            <p>Test your F1 start reaction time</p>
          </div>
        </div>
        <div class="footer-bottom">
          <p>© 2026 F1StartTimer.org | All rights reserved</p>
        </div>
      </div>
    </footer>

  </div>
</template>

<script>
import LightStrip from "./components/LightStrip.vue";

const IDLE = "idle";
const RUNNING = "running";
const WAITING = "waiting";
const LIGHT_ON_INTERVAL = 1000;

export default {
  components: { LightStrip },
  data() {
    return {
      state: IDLE,
      nextLightStrip: 0,
      result: "00.000",
      startTime: null,
      timerId: null,
      fuzzerId: null,
      best: localStorage.best || 0
    };
  },
  methods: {
    start() {
      this.nextLightStrip = 0;
      this.result = "00.000";
      this.startTime = null;
      this.clearLights();
      this.turnOnNextLight();
      this.timerId = setInterval(() => this.turnOnNextLight(), LIGHT_ON_INTERVAL);
    },
    turnOnNextLight() {
      if (this.nextLightStrip == 5) {
        this.fuzzedLightsOut();
        clearInterval(this.timerId);
      } else {
        this.$refs.lights[this.nextLightStrip].switchOn(true);
        this.nextLightStrip++;
      }
    },
    fuzzedLightsOut() {
      const fuzzyInterval = Math.random() * 1800 + 2400;
      this.fuzzerId = setTimeout(() => {
        this.clearLights();
        this.startTime = Date.now();
        this.state = WAITING;
      }, fuzzyInterval);
    },
    clearLights() {
      this.$refs.lights.forEach(l => l.switchOn(false));
    },
    onClick() {
      if (this.state == RUNNING) {
        this.state = IDLE;
        this.result = "JUMP START!";
        clearInterval(this.timerId);
        clearTimeout(this.fuzzerId);
      } else if (this.state == IDLE) {
        this.state = RUNNING;
        this.start();
      } else if (this.state == WAITING) {
        this.state = IDLE;
        const timeDiff = Date.now() - this.startTime;
        this.result = this.format(timeDiff);
        this.best = this.best === 0 ? timeDiff : Math.min(this.best, timeDiff);
        localStorage.best = this.best;
      }
    },
    format(ms) {
      const secs = (ms / 1000).toFixed(3);
      return `${(parseInt(secs) < 10 ? "0" : "") + secs}`;
    }
  }
};
</script>

<style scoped>
/* Base Styles */
* {
  box-sizing: border-box;
}

#app {
  font-family: 'Chakra Petch', sans-serif;
  color: #1E293B;
  background: #F8FAFC;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
}

/* Navigation */
.navbar {
  position: fixed;
  top: 0;
  width: 100%;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  padding: 16px 0;
}

.navbar .container {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.logo {
  font-family: 'Russo One', sans-serif;
  font-size: 24px;
  color: #2563EB;
  font-weight: bold;
}

.nav-links {
  display: flex;
  gap: 32px;
}

.nav-links a {
  color: #1E293B;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s;
}

.nav-links a:hover {
  color: #2563EB;
}

/* Hero Section */
.hero {
  padding: 120px 0 80px;
  text-align: center;
  cursor: pointer;
}

.hero h1 {
  font-family: 'Russo One', sans-serif;
  font-size: 48px;
  color: #1E293B;
  margin: 0 0 24px;
  line-height: 1.2;
}

.subtitle {
  font-size: 18px;
  line-height: 1.6;
  color: #475569;
  max-width: 800px;
  margin: 0 auto 48px;
}

.game-area {
  background: white;
  border-radius: 16px;
  padding: 48px 24px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.1);
  max-width: 800px;
  margin: 0 auto;
}

.help {
  margin-bottom: 32px;
}

.help p {
  color: #64748B;
  font-size: 16px;
}

#lights-container {
  position: relative;
  display: flex;
  flex-direction: row;
  justify-content: center;
  margin: 48px 0;
}

#connector {
  position: absolute;
  background: black;
  height: 3%;
  top: 50%;
  width: 80%;
  left: 10%;
  z-index: -1;
}

.time {
  font-family: 'Russo One', sans-serif;
  font-size: 64px;
  color: #2563EB;
  margin: 32px 0 16px;
  font-weight: 400;
}

.best-score {
  font-size: 20px;
  color: #64748B;
  font-weight: 500;
}

/* Responsive */
@media (max-width: 768px) {
  .nav-links {
    display: none;
  }

  .hero h1 {
    font-size: 32px;
  }

  .subtitle {
    font-size: 16px;
  }

  .time {
    font-size: 48px;
  }
}

/* How It Works Section */
.how-it-works {
  padding: 80px 0;
  background: white;
}

.how-it-works h2 {
  font-family: 'Russo One', sans-serif;
  font-size: 36px;
  text-align: center;
  margin-bottom: 16px;
}

.section-intro {
  text-align: center;
  font-size: 18px;
  color: #64748B;
  max-width: 700px;
  margin: 0 auto 48px;
}

.steps {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 32px;
  margin-bottom: 32px;
}

.step {
  text-align: center;
}

.step-number {
  width: 48px;
  height: 48px;
  background: #2563EB;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  font-weight: bold;
  margin: 0 auto 16px;
}

.step h3 {
  font-size: 20px;
  margin: 0 0 8px;
  color: #1E293B;
}

.step p {
  font-size: 14px;
  color: #64748B;
  line-height: 1.6;
}

.warning {
  text-align: center;
  color: #DC2626;
  font-size: 16px;
  margin-top: 32px;
}

/* Benchmark Section */
.benchmark {
  padding: 80px 0;
  background: #F8FAFC;
}

.benchmark h2 {
  font-family: 'Russo One', sans-serif;
  font-size: 36px;
  text-align: center;
  margin-bottom: 16px;
}

.benchmark-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 24px;
  margin: 48px 0;
}

.benchmark-card {
  background: white;
  padding: 32px;
  border-radius: 12px;
  text-align: center;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.benchmark-label {
  font-size: 14px;
  color: #64748B;
  margin-bottom: 8px;
}

.benchmark-time {
  font-family: 'Russo One', sans-serif;
  font-size: 32px;
  color: #2563EB;
  margin: 8px 0;
}

.benchmark-desc {
  font-size: 16px;
  color: #1E293B;
  font-weight: 500;
}

.benchmark-note {
  text-align: center;
  color: #64748B;
  max-width: 700px;
  margin: 0 auto;
  line-height: 1.6;
}

/* Tips Section */
.tips {
  padding: 80px 0;
  background: white;
}

.tips h2 {
  font-family: 'Russo One', sans-serif;
  font-size: 36px;
  text-align: center;
  margin-bottom: 16px;
}

.tips-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
  margin: 48px 0;
}

.tip-card {
  background: #F8FAFC;
  padding: 24px;
  border-radius: 12px;
  border-left: 4px solid #2563EB;
}

.tip-card h3 {
  font-size: 18px;
  color: #1E293B;
  margin: 0 0 12px;
}

.tip-card p {
  font-size: 14px;
  color: #64748B;
  line-height: 1.6;
  margin: 0;
}

.cta-link {
  display: block;
  text-align: center;
  color: #2563EB;
  font-weight: 600;
  text-decoration: none;
  margin-top: 32px;
  transition: color 0.2s;
}

.cta-link:hover {
  color: #1E40AF;
}

/* FAQ Section */
.faq {
  padding: 80px 0;
  background: #F8FAFC;
}

.faq h2 {
  font-family: 'Russo One', sans-serif;
  font-size: 36px;
  text-align: center;
  margin-bottom: 48px;
}

.faq-item {
  background: white;
  padding: 24px;
  border-radius: 12px;
  margin-bottom: 16px;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
}

.faq-item h3 {
  font-size: 18px;
  color: #1E293B;
  margin: 0 0 12px;
}

.faq-item p {
  font-size: 16px;
  color: #64748B;
  line-height: 1.6;
  margin: 0;
}

/* CTA Section */
.cta-section {
  padding: 80px 0;
  background: linear-gradient(135deg, #2563EB 0%, #3B82F6 100%);
  color: white;
  text-align: center;
}

.cta-section h2 {
  font-family: 'Russo One', sans-serif;
  font-size: 36px;
  margin-bottom: 16px;
}

.cta-section p {
  font-size: 18px;
  margin-bottom: 32px;
}

.cta-buttons {
  display: flex;
  gap: 16px;
  justify-content: center;
  flex-wrap: wrap;
}

.btn-primary, .btn-secondary {
  padding: 16px 32px;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.2s;
  display: inline-block;
}

.btn-primary {
  background: #F97316;
  color: white;
}

.btn-primary:hover {
  background: #EA580C;
  transform: translateY(-2px);
}

.btn-secondary {
  background: white;
  color: #2563EB;
}

.btn-secondary:hover {
  background: #F8FAFC;
  transform: translateY(-2px);
}

/* Footer */
.footer {
  padding: 48px 0 24px;
  background: #1E293B;
  color: white;
}

.footer-content {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 32px;
  margin-bottom: 32px;
}

.footer-col h4 {
  font-size: 16px;
  margin-bottom: 16px;
  color: white;
}

.footer-col a {
  display: block;
  color: #94A3B8;
  text-decoration: none;
  margin-bottom: 8px;
  transition: color 0.2s;
}

.footer-col a:hover {
  color: white;
}

.footer-col p {
  color: #94A3B8;
  font-size: 14px;
  margin: 8px 0;
}

.footer-bottom {
  text-align: center;
  padding-top: 24px;
  border-top: 1px solid #334155;
  color: #94A3B8;
  font-size: 14px;
}

.footer-bottom p {
  margin: 0;
}

/* Additional Responsive Styles */
@media (max-width: 768px) {
  .steps {
    grid-template-columns: 1fr;
  }

  .benchmark-cards {
    grid-template-columns: 1fr;
  }

  .tips-grid {
    grid-template-columns: 1fr;
  }

  .cta-buttons {
    flex-direction: column;
    align-items: center;
  }

  .btn-primary, .btn-secondary {
    width: 100%;
    max-width: 300px;
  }
}
</style>
